import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CLOSED_STATES, WORK_STATE_LABELS, type WorkState } from '@scc/shared';
import { fetchMe, fetchWorkItems, updateWorkItem, type Me, type WorkItem } from '../api';
import { TaskEditor } from './TaskEditor';
import { Badge, Empty, ErrorBox, Loading, Table } from '../components/ui';
import { Kanban, StateSummary } from '../components/kanban';
import { Adjuntos } from '../components/Adjuntos';

/** Presets del mapa de pantallas: los filtros que de verdad se usan. */
const PRESETS = [
  { label: 'Todo', params: {} },
  { label: 'P0 y P1', params: { priority: 'P0' } },
  { label: 'Vencidas', params: { overdue: 'true' } },
  { label: 'Bloqueadas', params: { state: 'blocked' } },
  { label: 'Errores', params: { kind: 'bug' } },
] as const;

export function TasksPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<'kanban' | 'table'>('kanban');
  const [selected, setSelected] = useState<WorkItem | null>(null);
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<WorkItem | null>(null);
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const filters = {
    project: params.get('project') ?? undefined,
    state: params.get('state') ?? undefined,
    kind: params.get('kind') ?? undefined,
    priority: params.get('priority') ?? undefined,
    overdue: params.get('overdue') ?? undefined,
  };

  const query = useQuery({
    queryKey: ['work-items', filters],
    queryFn: () => fetchWorkItems(filters),
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  const items = query.data;

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Tareas</h1>
      <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
        {items.length === 0
          ? 'Ningún item registrado con estos filtros. GitHub Issues está vacío en los siete repositorios, así que todo el trabajo se registra aquí.'
          : `${items.length} item(s) · ${items.filter((item) => item.isOverdue).length} vencido(s)`}
      </p>

      {me.data && (me.data.role === 'admin' || me.data.role === 'member') && (
        <button
          type="button"
          onClick={() => {
            // Se cierra el detalle, como ya hacía «editar». Con los dos paneles abiertos
            // había DOS manejadores de Ctrl+V escuchando —el de la tarea abierta y el del
            // alta— y una captura pegada se subía a la tarea existente además de quedar
            // puesta en la nueva.
            setSelected(null);
            setCreando(true);
          }}
          className="mb-4 rounded bg-[var(--color-ink)] px-3 py-1.5 text-[13px] text-[var(--color-surface)]"
        >
          Nueva tarea
        </button>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-1">
        {PRESETS.map((preset) => {
          const active =
            JSON.stringify(Object.fromEntries(params)) === JSON.stringify(preset.params);
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => setParams(preset.params)}
              className={`rounded px-2.5 py-1 text-[12px] ${
                active
                  ? 'bg-[var(--color-surface-muted)] font-medium'
                  : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
              }`}
            >
              {preset.label}
            </button>
          );
        })}

        <span className="ml-auto flex gap-1">
          {(['kanban', 'table'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setView(option)}
              className={`rounded px-2.5 py-1 text-[12px] ${
                view === option
                  ? 'bg-[var(--color-surface-muted)] font-medium'
                  : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
              }`}
            >
              {option === 'kanban' ? 'Tablero' : 'Tabla'}
            </button>
          ))}
        </span>
      </div>

      {items.length > 0 && (
        <div className="mb-4">
          <StateSummary items={items} />
        </div>
      )}

      {view === 'kanban' ? (
        <Kanban items={items} onSelect={setSelected} />
      ) : (
        <ItemTable items={items} onSelect={setSelected} />
      )}

      {selected && (
        <ItemPanel
          item={selected}
          me={me.data}
          onClose={() => setSelected(null)}
          onEditar={() => {
            setEditando(selected);
            setSelected(null);
          }}
        />
      )}

      {(creando || editando) && me.data && (
        <PanelLateral
          titulo={creando ? 'Nueva tarea' : 'Editar tarea'}
          onCerrar={() => {
            setCreando(false);
            setEditando(null);
          }}
        >
          <TaskEditor
            me={me.data}
            item={editando ?? undefined}
            onDone={() => {
              setCreando(false);
              setEditando(null);
            }}
            onCancel={() => {
              setCreando(false);
              setEditando(null);
            }}
          />
        </PanelLateral>
      )}
    </>
  );
}

export function ItemTable({
  items,
  onSelect,
}: {
  items: WorkItem[];
  onSelect?: (item: WorkItem) => void;
}): JSX.Element {
  if (items.length === 0) return <Empty>Nada que mostrar.</Empty>;

  return (
    <Table
      head={[
        'Proyecto',
        'Tipo',
        'Título',
        'Estado',
        'Pri.',
        'Track',
        'Responsable',
        'Objetivo',
        'Evid.',
      ]}
    >
      {items.map((item) => (
        <tr
          key={item.id}
          className="border-b border-[var(--color-border-subtle)] last:border-0 hover:bg-[var(--color-surface-muted)]"
        >
          <td className="px-3 py-2 text-[11px] text-[var(--color-ink-muted)]">
            {item.projectName}
          </td>
          <td className="px-3 py-2 text-[11px]">
            {item.kind === 'bug' ? 'error' : item.kind === 'feature' ? 'func.' : 'tarea'}
          </td>
          <td className="px-3 py-2">
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(item)}
                className="text-left hover:underline"
              >
                {item.title}
              </button>
            ) : (
              item.title
            )}
          </td>
          <td className="whitespace-nowrap px-3 py-2 text-[12px]">
            {WORK_STATE_LABELS[item.state as WorkState] ?? item.state}
          </td>
          <td className="px-3 py-2">
            <Badge
              tone={item.priority === 'P0' ? 'bad' : item.priority === 'P1' ? 'warn' : 'neutral'}
            >
              {item.priority}
            </Badge>
          </td>
          <td className="px-3 py-2 text-[11px]">{item.trackCode ?? item.trackName ?? '—'}</td>
          <td className="px-3 py-2 text-[11px]">{item.assigneeName ?? '—'}</td>
          <td className="whitespace-nowrap px-3 py-2 text-[11px]">
            {item.targetDate ? (
              <span className={item.isOverdue ? 'text-red-700 dark:text-red-400' : ''}>
                {item.targetDate}
              </span>
            ) : (
              '—'
            )}
          </td>
          <td className="px-3 py-2 text-[11px] tabular-nums">
            {item.closedWithoutEvidence ? (
              <span title="Completado sin ninguna evidencia enlazada">
                <Badge tone="warn">falta</Badge>
              </span>
            ) : (
              item.evidenceCount
            )}
          </td>
        </tr>
      ))}
    </Table>
  );
}

/** Panel de detalle. Muestra la procedencia y por qué algunos campos no se editan. */
export function ItemPanel({
  item,
  me,
  onClose,
  onEditar,
}: {
  item: WorkItem;
  /** Opcional: sin sesión conocida no se ofrece editar, en vez de ofrecerlo y fallar. */
  me?: Me;
  onClose: () => void;
  onEditar?: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-y-0 right-0 z-20 w-full max-w-md overflow-y-auto border-l border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-5 shadow-xl">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[15px] font-semibold leading-snug">{item.title}</h2>
        <div className="flex shrink-0 gap-3 text-[13px]">
          {onEditar &&
            me &&
            (me.role === 'admin' ||
              (me.role === 'member' &&
                (item.assigneeLogin ?? '').toLowerCase() === me.login.toLowerCase())) && (
              <button
                type="button"
                onClick={onEditar}
                className="text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
              >
                editar
              </button>
            )}
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
          >
            cerrar
          </button>
        </div>
      </div>

      <AccionesRapidas item={item} me={me} />

      {item.description && (
        <p className="mt-2 whitespace-pre-wrap text-[13px] leading-snug text-[var(--color-ink-muted)]">
          {item.description}
        </p>
      )}

      <dl className="mt-4 space-y-1.5 text-[12px]">
        <Row label="Proyecto">{item.projectName}</Row>
        <Row label="Estado">
          {WORK_STATE_LABELS[item.state as WorkState] ?? item.state}
          <span className="ml-1.5 opacity-60">
            ({item.stateSource === 'derived' ? 'derivado del código' : 'fijado a mano'})
          </span>
        </Row>
        <Row label="Prioridad">{item.priority}</Row>
        <Row label="Tipo">
          {item.kind === 'bug' ? 'Error' : item.kind === 'feature' ? 'Funcionalidad' : 'Tarea'}
        </Row>
        <Row label="Track">{item.trackCode ?? item.trackName ?? '—'}</Row>
        <Row label="Responsable">{item.assigneeName ?? 'sin asignar'}</Row>
        <Row label="Rama">
          {item.branchName ? <code className="font-mono text-[11px]">{item.branchName}</code> : '—'}
        </Row>
        <Row label="Objetivo">{item.targetDate ?? 'sin fecha'}</Row>
        <Row label="Evidencias">
          {item.evidenceCount} de {item.linkCount} enlace(s)
        </Row>
        <Row label="Procedencia">
          {item.origin === 'github'
            ? 'issue de GitHub'
            : item.origin === 'derived'
              ? 'importado del Markdown'
              : 'creado en el portal'}
        </Row>
        {item.sourceRef && (
          <Row label="Origen">
            <code className="font-mono text-[10px]">{item.sourceRef}</code>
          </Row>
        )}
      </dl>

      {item.origin === 'github' && (
        <p className="mt-4 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] px-3 py-2 text-[11px] leading-snug text-[var(--color-ink-muted)]">
          Este item viene de un issue de GitHub. Su título, estado, prioridad y responsable{' '}
          <strong className="font-semibold">se gestionan allí</strong>: editarlos aquí crearía dos
          verdades distintas y la siguiente sincronización borraría esta. Lo que sí se puede cambiar
          es el track, la fecha objetivo y el progreso.
        </p>
      )}

      {item.closedWithoutEvidence && (
        <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          ▲ Está marcado como completado pero no tiene ninguna evidencia enlazada: ni un pull
          request, ni un commit, ni un tag de despliegue. Es una afirmación sin respaldo.
        </p>
      )}

      {/* Mismo permiso que para editar: un admin, o quien tiene la tarea asignada. */}
      <Adjuntos
        workItemId={item.id}
        // Sin sesión no se sube nada —`puedeEditar` ya es falso—, así que estos ceros no
        // llegan a usarse. Se pasan así para no inventar aquí un límite que decide el
        // servidor.
        limites={{
          imagen: me?.attachmentsMaxMb ?? 0,
          video: me?.attachmentsVideoMaxMb ?? 0,
          documento: me?.attachmentsDocMaxMb ?? 0,
        }}
        puedeEditar={Boolean(
          me &&
          (me.role === 'admin' ||
            (me.role === 'member' &&
              (item.assigneeLogin ?? '').toLowerCase() === me.login.toLowerCase())),
        )}
      />
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex gap-3">
      <dt className="w-24 shrink-0 text-[var(--color-ink-muted)]">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}

/** Panel lateral reutilizable: mismo sitio y misma forma que el de detalle. */
export function PanelLateral({
  titulo,
  onCerrar,
  children,
}: {
  titulo: string;
  onCerrar: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="fixed inset-y-0 right-0 z-20 w-full max-w-md overflow-y-auto border-l border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-5 shadow-xl">
      <div className="mb-4 flex items-start justify-between gap-3">
        <h2 className="text-[15px] font-semibold">{titulo}</h2>
        <button
          type="button"
          onClick={onCerrar}
          className="shrink-0 text-[13px] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
        >
          cerrar
        </button>
      </div>
      {children}
    </div>
  );
}

/**
 * Marcar una tarea propia como empezada o terminada, en un clic.
 *
 * Antes esto se podía —un colaborador ya podía cambiar el estado de lo suyo— pero costaba
 * cuatro pasos: abrir la tarea, pulsar «editar», buscar el desplegable y guardar. La
 * acción MÁS frecuente de quien recibe trabajo no puede ser la más enterrada; cuando lo
 * es, la gente deja de actualizar el estado y el tablero empieza a mentir.
 *
 * Solo aparece para quien tiene la tarea asignada, o para un administrador. Y solo se
 * ofrece la transición que tiene sentido desde donde está: no se enseña «Empezar» en algo
 * ya terminado, ni «Completada» en algo que ya lo está.
 */
export function AccionesRapidas({
  item,
  me,
  onHecho,
}: {
  item: WorkItem;
  me?: Me;
  onHecho?: () => void;
}): JSX.Element | null {
  const client = useQueryClient();
  const cambiar = useMutation({
    mutationFn: (state: string) => updateWorkItem(item.id, { state }),
    onSuccess: () => {
      void client.invalidateQueries();
      onHecho?.();
    },
  });

  if (!me) return null;
  const suya = (item.assigneeLogin ?? '').toLowerCase() === me.login.toLowerCase();
  const puede = me.role === 'admin' || (me.role === 'member' && suya);
  if (!puede) return null;

  // Lo que viene de un issue de GitHub se gestiona allí: cambiarlo aquí duraría hasta la
  // siguiente sincronización, y una edición que se deshace sola es peor que un error.
  if (item.origin === 'github') return null;

  const acciones: Array<{ estado: string; etiqueta: string }> = [];
  if (item.state !== 'in_development' && !CLOSED_STATES.includes(item.state as WorkState)) {
    acciones.push({ estado: 'in_development', etiqueta: 'Empezar' });
  }
  if (item.state !== 'done') acciones.push({ estado: 'done', etiqueta: 'Marcar completada' });
  if (acciones.length === 0) return null;

  return (
    <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-3">
      <p className="mb-2 text-[12px] text-[var(--color-ink-muted)]">
        {suya ? 'Tu tarea' : 'Como administrador'} · ahora está en{' '}
        <strong>{WORK_STATE_LABELS[item.state as WorkState] ?? item.state}</strong>
      </p>
      <div className="flex flex-wrap gap-2">
        {acciones.map((a) => (
          <button
            key={a.estado}
            type="button"
            disabled={cambiar.isPending}
            onClick={() => cambiar.mutate(a.estado)}
            className="rounded bg-[var(--color-ink)] px-3 py-1.5 text-[13px] text-[var(--color-surface)] disabled:opacity-40"
          >
            {cambiar.isPending ? 'Guardando…' : a.etiqueta}
          </button>
        ))}
      </div>
      {cambiar.isError && <ErrorBox error={cambiar.error} />}
    </div>
  );
}
